import { Badge, Card, Spinner } from '@fc/ui'
import { useQuery } from '@tanstack/react-query'
import { ChevronRight, ListChecks } from 'lucide-react'
import { Link } from 'react-router-dom'
import { PageHeader } from '../../components/page-header'
import { getTeamHierarchy, type TeamNode } from '../../lib/teams-groups-api'

export function TeamsList() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['teams', 'hierarchy'],
    queryFn: getTeamHierarchy,
  })

  return (
    <div className="space-y-6">
      <PageHeader
        title="Teams"
        description="Your ministry hierarchy. Teams can nest to mirror how your org is structured."
      />

      <Card className="p-0">
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-20 text-muted">
            <Spinner /> Loading teams…
          </div>
        ) : isError ? (
          <div className="space-y-2 py-20 text-center">
            <p className="text-sm text-danger">Couldn’t load teams.</p>
            <p className="text-xs text-dim">
              {error instanceof Error ? error.message : 'Is the API running on :3000?'}
            </p>
          </div>
        ) : !data || data.length === 0 ? (
          <div className="py-20 text-center text-sm text-muted">
            No teams yet. Create your first ministry team to get started.
          </div>
        ) : (
          <ul className="divide-y divide-border-soft">
            {data.map((node) => (
              <TeamTreeRow key={node.id} node={node} depth={0} />
            ))}
          </ul>
        )}
      </Card>
    </div>
  )
}

function TeamTreeRow({ node, depth }: { node: TeamNode; depth: number }) {
  return (
    <>
      <li>
        <Link
          to={`/app/teams/${node.id}`}
          className="flex items-center gap-3 px-5 py-3 transition-colors hover:bg-card-hover focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring"
          style={{ paddingLeft: `${1.25 + depth * 1.5}rem` }}
        >
          <div className="grid size-8 shrink-0 place-items-center rounded-md bg-surface-2 text-secondary">
            <ListChecks className="size-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-medium text-fg">{node.name}</div>
            {node.siteName && <div className="text-xs text-dim">{node.siteName}</div>}
          </div>
          <div className="flex items-center gap-2 text-xs text-dim">
            {node.groupCount > 0 && (
              <Badge variant="secondary">
                {node.groupCount} group{node.groupCount === 1 ? '' : 's'}
              </Badge>
            )}
            {node.childTeamCount > 0 && (
              <Badge variant="neutral">
                {node.childTeamCount} sub-team{node.childTeamCount === 1 ? '' : 's'}
              </Badge>
            )}
            <ChevronRight className="size-4" />
          </div>
        </Link>
      </li>
      {node.children.map((child) => (
        <TeamTreeRow key={child.id} node={child} depth={depth + 1} />
      ))}
    </>
  )
}
